import { useCallback, useEffect, useMemo, useState } from "react";

import { DateRange } from "react-day-picker";

import { format } from "date-fns";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { fetchSellers } from "~/services/sellerService";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";

// Custom hook for debounced value
const useDebounce = <T>(value: T, delay: number = 300): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useOrderFilters = (
  salesRequests: SalesRequest[],
  clientNames: Record<string, string> = {},
  initialDateRange?: DateRange
) => {
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Date range state with defaults - use provided initial range or default to last 90 days for more flexibility
  const [dateRange, setDateRange] = useState<DateRange>(
    initialDateRange || {
      from: new Date(new Date().setDate(new Date().getDate() - 90)),
      to: new Date(new Date().setFullYear(new Date().getFullYear() + 1)), // Extend to include future dates up to 1 year
    }
  );

  // Client filter state
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(true);

  // Seller filter state
  const [sellers, setSellers] = useState<
    { id: string; name: string; lastname: string }[]
  >([]);
  const [selectedSellerIds, setSelectedSellerIds] = useState<string[]>([]);
  const [isLoadingSellers, setIsLoadingSellers] = useState(true);

  // Additional order attribute filters
  const [filterByAmount, setFilterByAmount] = useState<{
    min: number;
    max: number | null;
  } | null>(null);
  const [sortOrder, setSortOrder] = useState<{
    field: keyof SalesRequest;
    direction: "asc" | "desc";
  }>({
    field: "createdAt",
    direction: "desc",
  });

  // Get current user profile - use selectors to avoid rerendering on unrelated store changes
  const currentUserId = useProfileStore((state) => state.id);
  const userRole = useProfileStore((state) => state.role);

  // Fetch clients for the filter
  useEffect(() => {
    const fetchClients = async () => {
      setIsLoadingClients(true);
      try {
        let query = supabase
          .from("clients")
          .select("id, name")
          .is("deleted_at", null); // Exclude soft-deleted clients

        // If user is a seller, only fetch their clients
        if (userRole === "seller" && currentUserId) {
          query = query.eq("assigned_seller", currentUserId);
        }

        const { data, error } = await query.order("name");

        if (error) throw error;

        const clientOptions = data.map((client) => ({
          id: client.id,
          name: client.name,
        }));

        setClients(clientOptions);

        // Initially select all clients
        setSelectedClientIds(clientOptions.map((client) => client.id));
      } catch (error) {
        console.error("Error fetching clients:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los clientes",
          variant: "destructive",
        });
      } finally {
        setIsLoadingClients(false);
      }
    };

    fetchClients();
  }, [currentUserId, userRole]);

  // Fetch sellers for the filter
  useEffect(() => {
    const loadSellers = async () => {
      setIsLoadingSellers(true);
      try {
        const sellerData = await fetchSellers();

        setSellers(sellerData);

        // If user is a seller, only select themselves
        if (userRole === "seller" && currentUserId) {
          setSelectedSellerIds([currentUserId]);
        } else {
          // Otherwise, select all sellers
          setSelectedSellerIds(sellerData.map((seller) => seller.id));
        }
      } catch (error) {
        console.error("Error fetching sellers:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los vendedores",
          variant: "destructive",
        });
      } finally {
        setIsLoadingSellers(false);
      }
    };

    loadSellers();
  }, [currentUserId, userRole]);

  // Utility functions for filtering
  const matchesSearch = useCallback(
    (order: SalesRequest): boolean => {
      if (!debouncedSearchQuery) return true;

      const searchTerms = debouncedSearchQuery.toLowerCase().split(" ");
      const clientName = clientNames[order.clientId] || "";

      // Check if all search terms match
      return searchTerms.every(
        (term) =>
          order.id.toLowerCase().includes(term) ||
          clientName.toLowerCase().includes(term) ||
          order.clientId.toLowerCase().includes(term) ||
          order.createdBy.toLowerCase().includes(term) ||
          (order.comments && order.comments.toLowerCase().includes(term)) ||
          (order.factura_number &&
            order.factura_number.toLowerCase().includes(term))
      );
    },
    [debouncedSearchQuery, clientNames]
  );

  const matchesDateRange = useCallback(
    (order: SalesRequest): boolean => {
      // Handle case where dateRange is undefined or null
      if (!dateRange) {
        return true; // Show all orders if no date range is set
      }

      const orderDate = new Date(order.createdAt);

      return (
        (!dateRange.from || orderDate >= dateRange.from) &&
        (!dateRange.to ||
          orderDate <= new Date(dateRange.to.getTime() + 86400000))
      ); // Include end date fully
    },
    [dateRange]
  );

  const matchesClientFilter = useCallback(
    (order: SalesRequest): boolean => {
      return (
        selectedClientIds.length === 0 ||
        selectedClientIds.includes(order.clientId)
      );
    },
    [selectedClientIds]
  );

  const matchesSellerFilter = useCallback(
    (order: SalesRequest): boolean => {
      // If no sellers are selected, show all
      if (selectedSellerIds.length === 0) {
        return true;
      }

      // If the order creator is in the selected sellers list, include it
      if (selectedSellerIds.includes(order.createdBy)) {
        return true;
      }

      // If the order creator is not even in the sellers list (e.g., superadmin, logistica),
      // include it when all sellers are selected (which means we want to see everything)
      const isCreatorASeller = sellers.some(
        (seller) => seller.id === order.createdBy
      );
      const allSellersSelected = selectedSellerIds.length === sellers.length;

      if (!isCreatorASeller && allSellersSelected) {
        return true;
      }

      return false;
    },
    [selectedSellerIds, sellers]
  );

  const matchesAmountFilter = useCallback(
    (order: SalesRequest): boolean => {
      if (!filterByAmount) return true;

      const total = order.totalAmount || 0;
      const { min, max } = filterByAmount;

      return total >= min && (!max || total <= max);
    },
    [filterByAmount]
  );

  // Filter orders based on all criteria
  const filteredOrders = useMemo(() => {
    if (!salesRequests) return [];

    const filtered = salesRequests.filter((order) => {
      return (
        matchesSearch(order) &&
        matchesDateRange(order) &&
        matchesClientFilter(order) &&
        matchesSellerFilter(order) &&
        matchesAmountFilter(order)
      );
    });

    // Sort the filtered results
    return [...filtered].sort((a, b) => {
      const aValue = a[sortOrder.field];
      const bValue = b[sortOrder.field];

      // Handle null or undefined values
      if (!aValue && !bValue) return 0;
      if (!aValue) return sortOrder.direction === "asc" ? -1 : 1;
      if (!bValue) return sortOrder.direction === "asc" ? 1 : -1;

      // Compare dates
      if (sortOrder.field === "createdAt" || sortOrder.field === "updatedAt") {
        const dateA = new Date(aValue as string);
        const dateB = new Date(bValue as string);
        return sortOrder.direction === "asc"
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }

      // Compare numbers
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortOrder.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      // Compare strings
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });
  }, [
    salesRequests,
    matchesSearch,
    matchesDateRange,
    matchesClientFilter,
    matchesSellerFilter,
    matchesAmountFilter,
    sortOrder,
  ]);

  // Group orders by status according to the specified filter categories
  const groupedOrders = useMemo(() => {
    // Solicitada = pending and not paid
    const requestedOrders = filteredOrders.filter(
      (order) => order.status === "pending" && order.paid === false
    );

    // All approved orders (status=accepted regardless of payment)
    const approvedOrders = filteredOrders.filter(
      (order) => order.status === "accepted"
    );

    // Aprobada & Pagada (status=accepted, paid=true)
    const paidOrders = filteredOrders.filter(
      (order) => order.status === "accepted" && order.paid === true
    );

    // Aprobada & Pendiente de Pago (status=accepted, paid=false)
    const pendingPaymentOrders = filteredOrders.filter(
      (order) => order.status === "accepted" && order.paid === false
    );

    // Rejected orders
    const rejectedOrders = filteredOrders.filter(
      (order) => order.status === "rejected"
    );

    return {
      requestedOrders,
      approvedOrders,
      rejectedOrders,
      paidOrders,
      pendingPaymentOrders,
    };
  }, [filteredOrders]);

  // Create a summary of applied filters for UI display
  const filterSummary = useMemo(() => {
    const summary = [];

    if (debouncedSearchQuery) {
      summary.push(`Búsqueda: "${debouncedSearchQuery}"`);
    }

    if (dateRange?.from && dateRange?.to) {
      summary.push(
        `Fechas: ${format(dateRange.from, "dd/MM/yyyy")} - ${format(dateRange.to, "dd/MM/yyyy")}`
      );
    }

    if (
      selectedClientIds.length > 0 &&
      selectedClientIds.length < clients.length
    ) {
      summary.push(`${selectedClientIds.length} clientes seleccionados`);
    }

    if (
      selectedSellerIds.length > 0 &&
      selectedSellerIds.length < sellers.length
    ) {
      summary.push(`${selectedSellerIds.length} vendedores seleccionados`);
    }

    if (filterByAmount) {
      const { min, max } = filterByAmount;
      summary.push(`Monto: ${min}${max ? ` - ${max}` : "+"}`);
    }

    return summary;
  }, [
    debouncedSearchQuery,
    dateRange,
    selectedClientIds,
    clients.length,
    selectedSellerIds,
    sellers.length,
    filterByAmount,
  ]);

  // Reset all filters to default
  const resetFilters = useCallback(() => {
    setSearchQuery("");
    setDateRange(
      initialDateRange || {
        from: new Date(new Date().setDate(new Date().getDate() - 90)),
        to: new Date(),
      }
    );
    setSelectedClientIds(clients.map((client) => client.id));
    setSelectedSellerIds(sellers.map((seller) => seller.id));
    setFilterByAmount(null);
    setSortOrder({
      field: "createdAt",
      direction: "desc",
    });
  }, [clients, sellers, initialDateRange]);

  return {
    // Search
    searchQuery,
    setSearchQuery,

    // Date
    dateRange,
    setDateRange,

    // Clients
    clients,
    selectedClientIds,
    setSelectedClientIds,
    isLoadingClients,

    // Sellers
    sellers,
    selectedSellerIds,
    setSelectedSellerIds,
    isLoadingSellers,

    // Additional filters
    filterByAmount,
    setFilterByAmount,
    sortOrder,
    setSortOrder,

    // Filter operations
    resetFilters,
    filterSummary,

    // Results
    filteredOrders,
    ...groupedOrders,
  };
};
